/**
 * The preconditions of a channel modifier (schema v8): conditions on a
 * channel's current state, every one of which must hold for the modifier to
 * act. When one does not, the source command passes through as if the modifier
 * did not exist.
 *
 * A controlled component: the Channel Modifiers step owns the rows, and the
 * rules (what a row needs before it can be saved) live in `channelModifierV8`.
 * Every control carries `data-doc-label`, so the documentation scanner keys it
 * by its caption rather than by its position.
 */
import React from "react";
import { Button, Select } from "antd";
import { useTranslation } from "react-i18next";
import FlatbufferMessagePicker from "../../../components/select/FlatbufferMessagePicker";
import type { FbMessageRef } from "../../../../interfaces/FbMessage.interface";
import { ActiveState } from "../../../../flatbuffers/generated/archive/active-state";
import { PreconditionUnknown } from "../../../../flatbuffers/generated/archive/precondition-unknown";
import { SequenceStepScope } from "../../../../flatbuffers/generated/archive/sequence-step-scope";
import { StepPositionSource } from "../../../../flatbuffers/generated/archive/step-position-source";
import { NEW_PRECONDITION, type PreconditionRow } from "../channelModifierV8";

type Row = PreconditionRow<FbMessageRef>;

interface ModifierPreconditionsEditorProps {
  value: Row[];
  /** Every channel on the source's server (`preconditionChannelOptions`). */
  channelOptions: { label: string; value: string }[];
  /** The leaves a channel's adapters map, for its message picker. */
  messageFilterFor: (channelId: string) => Set<string> | undefined;
  onChange: (rows: Row[]) => void;
}

const fieldStyle: React.CSSProperties = { minWidth: 150, flex: 1 };
const captionStyle: React.CSSProperties = { display: "block" };

/** The Preconditions section: one row per condition, and Add Precondition. */
export default function ModifierPreconditionsEditor({
  value,
  channelOptions,
  messageFilterFor,
  onChange,
}: ModifierPreconditionsEditorProps): React.JSX.Element {
  const [t] = useTranslation();
  const setRow = (idx: number, row: Row) => onChange(value.map((r, i) => (i === idx ? row : r)));
  return (
    <div data-testid="modifier-preconditions-section" style={{ marginBottom: 8 }}>
      <span className="generalStyles__inputLabel">{t("Preconditions")}</span>
      <div style={{ color: "#888", fontSize: 12, marginTop: 4, marginBottom: 8 }}>
        {t(
          "The modifier acts only when every precondition holds on its channel now; otherwise the source command passes through untouched.",
        )}
      </div>
      {value.map((row, idx) => (
        <div
          key={idx}
          data-testid={`modifier-precondition-${idx}`}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "flex-end",
            marginBottom: 8,
          }}
        >
          <div style={fieldStyle}>
            <span className="generalStyles__inputLabel" style={captionStyle}>
              {t("Channel")}
            </span>
            <Select
              data-doc-label="Precondition Channel"
              data-testid={`modifier-precondition-channel-${idx}`}
              style={{ width: "100%" }}
              value={row.channelId || undefined}
              placeholder={t("Select channel")}
              options={channelOptions}
              // A different channel maps different messages, so the pick goes.
              onChange={(v: string) =>
                setRow(idx, { ...row, channelId: v ?? "", messageRef: null })
              }
            />
          </div>
          <div
            style={{ ...fieldStyle, flex: 2 }}
            data-testid={`modifier-precondition-message-${idx}`}
          >
            <span className="generalStyles__inputLabel" style={captionStyle}>
              {t("Message")}
            </span>
            <FlatbufferMessagePicker
              data-doc-label="Precondition Message"
              value={row.messageRef}
              disabled={!row.channelId}
              placeholder={row.channelId ? "Select message" : "Select a channel first"}
              messageValueFilter={row.channelId ? messageFilterFor(row.channelId) : undefined}
              onChange={(ref) => setRow(idx, { ...row, messageRef: ref })}
              allowClear
            />
          </div>
          <div style={fieldStyle}>
            <span className="generalStyles__inputLabel" style={captionStyle}>
              {t("Expected Level")}
            </span>
            <Select
              data-doc-label="Precondition Level"
              data-testid={`modifier-precondition-level-${idx}`}
              style={{ width: "100%" }}
              value={row.expectedActive}
              onChange={(v: ActiveState) => setRow(idx, { ...row, expectedActive: v })}
              options={[
                { label: "Active", value: ActiveState.ACTIVE },
                { label: "Inactive", value: ActiveState.INACTIVE },
              ]}
            />
          </div>
          <div style={fieldStyle}>
            <span className="generalStyles__inputLabel" style={captionStyle}>
              {t("Scope")}
            </span>
            <Select
              data-doc-label="Precondition Scope"
              data-testid={`modifier-precondition-scope-${idx}`}
              style={{ width: "100%" }}
              value={row.scope}
              onChange={(v: SequenceStepScope) => setRow(idx, { ...row, scope: v })}
              options={[
                { label: "Same floor", value: SequenceStepScope.SAME_POSITION },
                { label: "Any floor", value: SequenceStepScope.ANY_POSITION },
                { label: "Same floor + car", value: SequenceStepScope.SAME_POSITION_AND_LIFT },
              ]}
            />
          </div>
          {row.scope !== SequenceStepScope.ANY_POSITION && (
            <div style={fieldStyle}>
              <span className="generalStyles__inputLabel" style={captionStyle}>
                {t("Floor Source")}
              </span>
              <Select
                data-doc-label="Precondition Floor Source"
                data-testid={`modifier-precondition-floor-source-${idx}`}
                style={{ width: "100%" }}
                value={row.positionSource}
                onChange={(v: StepPositionSource) => setRow(idx, { ...row, positionSource: v })}
                options={[
                  { label: "Payload's floor", value: StepPositionSource.PAYLOAD },
                  { label: "Command's floor", value: StepPositionSource.SOURCE },
                ]}
              />
            </div>
          )}
          <div style={fieldStyle}>
            <span className="generalStyles__inputLabel" style={captionStyle}>
              {t("If Unknown")}
            </span>
            <Select
              data-doc-label="Precondition If Unknown"
              data-testid={`modifier-precondition-if-unknown-${idx}`}
              style={{ width: "100%" }}
              value={row.ifUnknown}
              onChange={(v: PreconditionUnknown) => setRow(idx, { ...row, ifUnknown: v })}
              options={[
                { label: "Assume not met", value: PreconditionUnknown.ASSUME_NOT_MET },
                { label: "Assume met", value: PreconditionUnknown.ASSUME_MET },
              ]}
            />
          </div>
          <Button
            data-doc-label="Remove Precondition"
            data-testid={`modifier-precondition-remove-${idx}`}
            size="small"
            danger
            onClick={() => onChange(value.filter((_, i) => i !== idx))}
          >
            {t("Remove")}
          </Button>
        </div>
      ))}
      <Button
        data-doc-label="Add Precondition"
        data-testid="modifier-precondition-add"
        size="small"
        onClick={() => onChange([...value, { ...NEW_PRECONDITION }])}
      >
        {t("Add Precondition")}
      </Button>
    </div>
  );
}
